import { useState, useEffect } from 'react';
import { Calendar, Home, IndianRupee, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MyListings({ currentUser }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyListings();
  }, [currentUser]);

  const fetchMyListings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/off-season/listings/my/${currentUser.id}`);
      const data = await response.json();
      setListings(data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (listingId, currentStatus) => {
    try {
      await fetch(`${BACKEND_URL}/api/off-season/listings/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_published: !currentStatus
        })
      });
      fetchMyListings();
    } catch (error) {
      console.error('Error toggling publish status:', error);
    }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/off-season/listings/${listingId}`, {
        method: 'DELETE'
      });
      fetchMyListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-600">Loading your listings...</p>
        </CardContent>
      </Card>
    );
  }

  if (listings.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Listings Yet</h3>
          <p className="text-gray-600 mb-6">Create your first off-season listing to start connecting with other resorts.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">My Off-Season Listings ({listings.length})</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {listings.map((listing) => (
          <Card key={listing.id} className={!listing.is_published ? 'opacity-60 border-dashed' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{listing.resort_name}</CardTitle>
                  <p className="text-sm text-gray-600">{listing.location}, {listing.state}</p>
                </div>
                <Badge className={listing.is_published ? 'bg-emerald-600' : 'bg-gray-400'}>
                  {listing.is_published ? 'Published' : 'Unpublished'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Months */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Off-Season Months
                </p>
                <div className="flex flex-wrap gap-2">
                  {listing.off_season_months.map((month) => (
                    <Badge key={month} variant="outline" className="text-xs">
                      {month}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    <Home className="h-4 w-4 inline mr-1" />
                    Rooms
                  </p>
                  <p className="font-semibold">{listing.rooms_available}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">
                    <IndianRupee className="h-4 w-4 inline mr-1" />
                    Min. Rent
                  </p>
                  <p className="font-semibold">₹{listing.minimum_rent.toLocaleString()}/night</p>
                </div>
              </div>

              {/* Optional Notes */}
              {listing.optional_notes && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-700">{listing.optional_notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-2 pt-4 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => togglePublish(listing.id, listing.is_published)}
                  className="flex-1"
                >
                  {listing.is_published ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Unpublish
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Publish
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteListing(listing.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default MyListings;
